/**
 * useZipAutofill — wire a ZIP input to auto-fill its sibling
 * city/state inputs from the MA ZIP lookup endpoint.
 *
 * Usage in a controlled form:
 *   const { handleZipChange } = useZipAutofill({
 *     formData,
 *     setField,   // (fieldName, value) => void
 *     zipField: 'zipcode',
 *     cityField: 'city',
 *     stateField: 'state',
 *   });
 *   ...
 *   <Input value={formData.zipcode} onChange={(e) => handleZipChange(e.target.value)} />
 *
 * Behavior:
 *   - Debounces the lookup (400ms) so we don't spam the API while typing.
 *   - Only fills city/state if they are currently empty — never overwrites
 *     user-entered values (silent corrections feel like data loss).
 *   - Silently no-ops on 404/network errors (returns the existing soft
 *     non-MA warning UI to do the explaining).
 *
 * Created June 2026 — pairs with /api/lookup/zip/{zip}.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useZipAutofill({ formData, setField, zipField, cityField, stateField }) {
  const [autofillStatus, setAutofillStatus] = useState(null); // 'looking' | 'filled' | 'unknown' | null
  const debounceRef = useRef(null);
  const lastQueriedRef = useRef('');

  const handleZipChange = useCallback((rawZip) => {
    // Always write the user's keystroke through first.
    setField(zipField, rawZip);
    setAutofillStatus(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const base = (rawZip || '').replace(/[^0-9]/g, '').slice(0, 5);
    if (base.length < 5) return;
    if (base === lastQueriedRef.current) return;

    debounceRef.current = setTimeout(async () => {
      lastQueriedRef.current = base;
      setAutofillStatus('looking');
      try {
        const r = await axios.get(`${API}/lookup/zip/${base}`);
        const city = r.data?.city;
        const state = r.data?.state;
        let filledSomething = false;
        if (city && !(formData?.[cityField] || '').trim()) {
          setField(cityField, city);
          filledSomething = true;
        }
        if (state && !(formData?.[stateField] || '').trim()) {
          setField(stateField, state);
          filledSomething = true;
        }
        setAutofillStatus(filledSomething ? 'filled' : null);
      } catch (_) {
        // 404 (non-MA) or network error.  Soft non-MA warning lives
        // in the existing JSX next to the ZIP input — no toast here.
        setAutofillStatus('unknown');
      }
    }, 400);
  }, [formData, setField, zipField, cityField, stateField]);

  // Clear debounce on unmount.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return { handleZipChange, autofillStatus };
}
